/* eslint-disable prettier/prettier */
/* eslint-disable no-prototype-builtins */

import BigNumber from 'bignumber.js';
import { ethers } from 'ethers';
import _ from 'lodash';
import { Hash } from 'viem';
import { PublicClient } from 'wagmi';

import { EPrecision } from '~components/types';
import { ActiveCollateralAssets } from '~config/protocol';
import { getRPCConfig } from '~config/rpcConfig';
import { NetworkIds } from '~config/rpcConfig';
import { AllVestaAssets, ENativeAsset, EVestaAsset, VestaFarm } from '~config/types';
import { ContractObjects, VestaRewards } from '~services/types';

import { makeThrowable } from './error/errorHandlers';
import { EVestaErrorType } from './error/errors';

/**
 * Store utility to modify the store state from a variable payload. Will take the payload's properties and modify corresponding state properties
 * @param correspondingObj State object to be modified.
 * @param payload New data meant for the state.
 */
export function updateObjData(correspondingObj: any, payload: any): void {
  for (const property in payload) {
    if (correspondingObj.hasOwnProperty(property)) {
      if (payload[property] !== undefined) correspondingObj[property] = payload[property];
    }
  }
}

/**
 * Store utility to modify the store state from a function. Will take the mutateKeys list and modify corresponding state properties
 * @param obj State object to be modified.
 * @param payloadObj New data meant for the state.
 */
export function mutateObjData(obj: any, payloadObj: any, mutate: (val: any, key: string) => any): void {
  for (const property in payloadObj) {
    if (obj.hasOwnProperty(property)) {
      obj[property] = mutate(obj[property], property);
    }
  }
}

/**
 * Determines the current fee on a borrow.
 * @param amountToBorrow The amount to borrow in USD.
 * @param previousAmount The previous amount borrowed (this can be 0 if no previous amount was borrowed.)
 * @param rate The floating point value of the borrowing rate.
 * @returns the amount of fees to be paid on a certain amount to borrow, in a string.
 */
export function determineBorrowingFee(amountToBorrow: number, previousAmount: number, rate: number): number {
  return amountToBorrow > previousAmount ? (amountToBorrow - previousAmount) * rate : 0;
}

/**
 * Sets a store inital state fields from the assets in `EAssetType`.
 * @param initialStateValue Value to set the asset fields to.
 * @param enumState
 * @returns a populated initial state with the current assets.
 */
export function setInitialState(enumState: any, initialStateValue: any | 'random' | 'randomSmall'): any {
  const initState: any = {};

  const getValue = () => {
    switch (initialStateValue) {
      case 'random':
        return _.random(420, 2000);
      case 'randomSmall':
        return _.random(0.1, 1);
      default:
        return initialStateValue;
    }
  };

  for (const enumValue of Object.values(enumState)) {
    Object.assign(initState, {
      [enumValue as AllVestaAssets]: enumValue === ENativeAsset.VST ? 1 : getValue(),
    });
  }

  return initState;
}

export function createAssetSends<ContractType>(contract: (asset: EVestaAsset) => ContractType) {
  return Object.fromEntries(
    ActiveCollateralAssets.map(asset => [asset, contract(asset)]),
  ) as unknown as ContractObjects<ContractType>;
}

export function createLpSends<ContractType>(contract: (farm: VestaFarm) => ContractType) {
  return Object.fromEntries(
    Object.values(VestaFarm).map(farm => [farm, contract(farm)]),
  ) as unknown as ContractObjects<ContractType, VestaFarm>;
}
/**
 * Utility meant for displaying and formatting numbers to string percent in the front-end.
 * @param value ether string.
 * @returns `value` in percent with no decimals and with the '%' sign. (If % is a fraction, fix it to 2 digits)
 */
export function toPercent(value: number): string {
  const percent = value * 100;
  if (percent < 1) return percent.toFixed(2) + ' %';
  else return Math.round(percent) + '%';
}

/**
 * Rounds an ether string down to a number of decimals.
 * @param amount The ether string to truncate.
 * @param decimals The amount of decimals you want after the dot.
 * @param type The math function to use. default it 'round'
 * @returns a number, with the number `decimals` after the dot
 */
export function roundNumber(
  amount: number,
  decimals: number,
  type: 'round' | 'floor' | 'ceil' = 'round',
): number {
  const multiplier: number = Math.pow(10, decimals || 0);
  return isNaN(amount) ? 0 : Math[type](amount * multiplier) / multiplier;
}

/**
 * Utility that registers contracts events from a predefined bank of functions.
 * @param contract Contract object which events you want to register.
 * @param eventBank Predefined events.
 * @param asset Optional arg for specifying which asset you registering the events for.
 */
export function registerContractEvents(
  contract: ethers.Contract,
  eventBank: any,
  net: NetworkIds,
  userAddress?: string,
  asset?: AllVestaAssets | VestaFarm,
): void {
  const events: any = asset ? eventBank(asset, net, userAddress) : eventBank(net, userAddress);

  try {
    for (const property in events) {
      contract.on(property, events[property]);
    }
  } catch (err) {
    console.warn(err);
    throw makeThrowable((err as any).value, EVestaErrorType.INTERNAL, asset);
  }
}

export const ROUND_DOWN_FACTOR = 0.000001;

/**
 * Calculated the maximum amount of debt a user can take.
 * @param amountCollateral The collateral amount in ether.
 * @param collateralPrice The collateral price in string decimals.
 * @returns maximum amount of debt in a string.
 */
export function determineMaxDebt({
  amountCollateral,
  collateralPrice,
  cr,
}: {
  amountCollateral: number;
  collateralPrice: number;
  cr: number;
}): number {
  return Math.max((amountCollateral * collateralPrice) / cr - ROUND_DOWN_FACTOR, 0);
}

export function determineMinCollateral({
  debt,
  collateralPrice,
  mcr,
}: {
  debt: number;
  collateralPrice: number;
  mcr: number;
}): number {
  return Math.max((debt * mcr) / collateralPrice, 0);
}

/**
 * Determines the collateral price in USD.
 * @param assetAmount The unit amount of asset
 * @param assetPrice The price of the collateral in USD.
 * @returns the USD value of the current collateral amount with arbitrary precision, in a string.
 */
export function determineAssetPriceUSD(assetAmount: number, assetPrice: number): number {
  return roundNumber(assetAmount * assetPrice, 2);
}

export function determineCollateralRatio(coll: bigint, collPrice: number, debt: bigint): number {
  const collateralRatio =
    bnum(coll).times(collPrice.toString()).dividedBy(debt.toString()).times(100).toNumber() || 0;
  return collateralRatio;
}

export function purifyMax(value: string, max: string): string {
  return parseFloat(max) < parseFloat(value) ? max : value;
}

export function floorTwoDecimal(debt: number): string {
  return '' + Math.floor(debt * 100) / 100;
}

/**
 * Determines the liquidation price of a current borrow provided the collateral amount and the debt amount.
 * @param recovery Sets the minimum ratio to be used.
 * @param collateral Current amount of collateral in ether.
 * @param debt Current amount of debt.
 * @returns the liquidation price in USD for the selected 'recovery' arg, in a string.
 */
export function determineLiquidation({
  collateral,
  debt,
  mcr,
}: {
  collateral: number;
  debt: number;
  mcr: number;
}): number {
  const RATIO = collateral / debt;

  return mcr / RATIO;
}

/**
 * Checks if an input is a number or a floating point number. Will only accept 1 dot.
 * @param input Input to validate
 * @returns a boolean depending on if input is valid.
 */
export function validateInput(input: string): boolean {
  return !!input.match(/^[0-9]*\.?[0-9]*$/);
}

/**
 * Purifies the input value into a number. Checks for 00, .
 * @param precision Precision (WEI : 18 digits, USD: 2 digits)
 * @param value Input value to purify
 * @returns perfect float number string.
 */
export function purifyIntoNumber(value: string, precision: EPrecision): string {
  let finalValue =
    precision === EPrecision.USD
      ? value.replace(/([^\d]*)(\d*(\.\d{0,2})?)(.*)/, '$2')
      : value.replace(/([^\d]*)(\d*(\.\d{0,18})?)(.*)/, '$2');
  if (finalValue === '' || finalValue === '00') {
    finalValue = '0';
  }
  if (finalValue.length > 1 && finalValue.charAt(0) === '0' && finalValue.charAt(1) !== '.') {
    finalValue = finalValue.substring(1);
  }
  return finalValue;
}

/**
 * Returns the tx URL string according to the rpc config.
 * @param txRes The immediate response from the operation.
 * @returns the tx URL.
 */
export function getTxUrl(txRes: any): string {
  const blockExplorer: string = getRPCConfig().blockExplorers?.default.url ?? '';

  return blockExplorer + 'tx/' + txRes.hash;
}

/**
 * Checks if the tx passed or failed, once it has been confirmed.
 * @param res The immediate response from the operation.
 * @returns a Promise<boolean> if the tx passed or not.
 */
export async function getTransactionBlock(hash: Hash, publicClient: PublicClient): Promise<number> {
  let block;
  const txReceipt = await retryWithExponentialBackoff<typeof publicClient.getTransactionReceipt>(
    () => publicClient.getTransactionReceipt({ hash }),
    5,
  );

  if (!txReceipt) {
    const txResponse: any = await publicClient.waitForTransactionReceipt({ hash });
    block = txResponse.blockNumber;
  } else {
    block = txReceipt.blockNumber;
  }

  return block as number;
}

/**
 * Sums the token rewards of a VestaReward object.
 * @param rewards VestaReward object to sum.
 * @returns the sum of the rewards.
 */
export function sumRewards(rewards: VestaRewards): number {
  let sum = 0;

  for (const assetReward of Object.values(rewards)) {
    sum += assetReward;
  }

  return sum;
}

export function truncateStringDecimals(number: string, decimals: number) {
  const dotPos = number.indexOf('.');

  if (dotPos !== -1) {
    return number.substring(0, dotPos + decimals + 1);
  }

  return number;
}
export function bnum(val: string | number | BigNumber | ethers.BigNumber | bigint): BigNumber {
  const number = typeof val === 'string' ? val : val ? val.toString() : '0';
  return new BigNumber(number);
}

export const GAS_LIMIT_MULTIPLE_FACTOR = BigInt(1);

export function defaultIncreaseGasLimit(gas: bigint) {
  return gas * GAS_LIMIT_MULTIPLE_FACTOR;
}

export const BIG_INT_ZERO = BigInt(0);
export const BIG_INT_ONE = BigInt(1);

export function retryWithExponentialBackoff<T extends (...args: any[]) => any>(
  fn: () => ReturnType<T>,
  retriesLeft: number,
  delay = 1000,
  maxDelay = 16000,
): Promise<ReturnType<T> | undefined> {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error: any) => {
        if (retriesLeft === 0) {
          reject(error); // Max retries reached, reject with the last error
        } else {
          setTimeout(() => {
            const newDelay = Math.min(delay * 2, maxDelay);
            retryWithExponentialBackoff(fn, retriesLeft - 1, newDelay, maxDelay)
              .then(resolve)
              .catch(reject);
          }, delay);
        }
      });
  });
}
